import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Avatar from './Avatar';
import './UserSelect.scss';

/**
 * UserSelect — 共用人員選取元件（多選 / 按部門分組）
 *
 * @param {string[]} values - 已選取的 user IDs
 * @param {function} onChange - (newValues) => void
 * @param {Array} users - [{ id, name, avatar_url, department_name }]
 * @param {string} placeholder
 */
const UserSelect = ({ values = [], onChange, users = [], placeholder = '選擇人員...', defaultOpen = false, singleSelect = false, excludeIds = [], portal = false, onClose }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');
  const [portalPos, setPortalPos] = useState(null);
  const ref = useRef(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // portal 模式:dropdown 在 body,ref 不含它,額外判斷
        if (portal && dropdownRef.current?.contains(e.target)) return;
        setOpen(false);
        onClose?.();
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, portal]);

  useEffect(() => {
    if (open) {
      setSearch('');
      if (portal && ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 280 && rect.top > spaceBelow; // 下方空間不足且上方較多 → 往上開
        const width = Math.max(rect.width, 240);
        const overflowRight = rect.left + width > window.innerWidth;
        // 明確設 top/bottom/left/right(不用的設 auto),避免 scss 的 absolute top/left/right 殘留衝突
        setPortalPos({
          position: 'fixed', minWidth: width, zIndex: 9500,
          left: overflowRight ? 'auto' : rect.left,
          right: overflowRight ? window.innerWidth - rect.right : 'auto',
          top: openUp ? 'auto' : rect.bottom + 4,
          bottom: openUp ? window.innerHeight - rect.top + 4 : 'auto',
        });
      }
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, portal]);

  const selectedItems = users.filter((u) => values.includes(u.id));

  // 排除指定用戶後再搜尋過濾
  const availableUsers = useMemo(() => {
    if (excludeIds.length === 0) return users;
    const excludeSet = new Set(excludeIds);
    return users.filter((u) => !excludeSet.has(u.id));
  }, [users, excludeIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableUsers;
    return availableUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.department_name || '').toLowerCase().includes(q)
    );
  }, [availableUsers, search]);

  // 按部門分組，同部門內按姓名排序
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((u) => {
      const dept = u.department_name || '未分類';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(u);
    });
    // 每組內按 name 排序
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant'))
    );
    // 按部門名稱排序（未分類排最後）
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '未分類') return 1;
      if (b === '未分類') return -1;
      return a.localeCompare(b, 'zh-Hant');
    });
  }, [filtered]);

  const toggle = (userId) => {
    if (singleSelect) {
      onChange([userId]);
      setOpen(false);
      return;
    }
    if (values.includes(userId)) {
      onChange(values.filter((v) => v !== userId));
    } else {
      onChange([...values, userId]);
    }
  };

  // 部門全選/全取消
  const toggleDepartment = (members) => {
    if (singleSelect) return;
    const memberIds = members.map((u) => u.id);
    const allSelected = memberIds.every((id) => values.includes(id));
    if (allSelected) {
      // 取消該部門全部
      onChange(values.filter((v) => !memberIds.includes(v)));
    } else {
      // 加入該部門尚未選取的
      const newValues = [...values];
      memberIds.forEach((id) => {
        if (!newValues.includes(id)) newValues.push(id);
      });
      onChange(newValues);
    }
  };

  const totalFiltered = grouped.reduce((sum, [, arr]) => sum + arr.length, 0);

  return (
    <div className={`user-select ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className="user-select-trigger"
        onClick={() => setOpen(!open)}
      >
        {selectedItems.length > 0 ? (
          <span className="user-select-summary">
            {selectedItems.slice(0, 3).map((s) => (
              <span className="user-select-chip" key={s.id}>
                <Avatar avatarUrl={s.avatar_url} name={s.name} size="xsmall" />
                <span className="user-select-chip-name">{s.name}</span>
              </span>
            ))}
            {selectedItems.length > 3 && <span className="user-select-more">+{selectedItems.length - 3}</span>}
          </span>
        ) : (
          <span className="user-select-placeholder">{placeholder}</span>
        )}
        <svg className="user-select-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (!portal || portalPos) && ((dd) => (portal ? createPortal(dd, document.body) : dd))(
        <div
          className="user-select-dropdown"
          ref={dropdownRef}
          style={portal && portalPos ? portalPos : undefined}
        >
          <div className="user-select-search">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#999" strokeWidth="1.5">
              <circle cx="6" cy="6" r="4" />
              <path d="M9.5 9.5l3 3" strokeLinecap="round" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜尋人員或部門..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {totalFiltered === 0 ? (
            <div className="user-select-empty">找不到符合的結果</div>
          ) : (
            <div className="user-select-groups">
              {grouped.map(([deptName, members]) => {
                const deptAllSelected = !singleSelect && members.every((u) => values.includes(u.id));
                const deptSomeSelected = !singleSelect && !deptAllSelected && members.some((u) => values.includes(u.id));
                return (
                <div key={deptName} className="user-select-group">
                  <div
                    className={`group-label ${!singleSelect ? 'clickable' : ''}`}
                    onClick={() => toggleDepartment(members)}
                  >
                    {!singleSelect && (
                      <span className="group-check">
                        {deptAllSelected ? (
                          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                            <rect x="1" y="1" width="12" height="12" rx="3" fill="#667eea" />
                            <path d="M4 7l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : deptSomeSelected ? (
                          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                            <rect x="1" y="1" width="12" height="12" rx="3" fill="#667eea" opacity="0.5" />
                            <path d="M4 7h6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                            <rect x="1.5" y="1.5" width="11" height="11" rx="2.5" stroke="#ccc" />
                          </svg>
                        )}
                      </span>
                    )}
                    {deptName}
                  </div>
                  {members.map((user) => {
                    const isSelected = values.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        className={`user-select-option ${isSelected ? 'selected' : ''} ${singleSelect ? 'single' : ''}`}
                        onClick={() => toggle(user.id)}
                      >
                        {!singleSelect && (
                          <span className="option-check">
                            {isSelected ? (
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <rect x="1" y="1" width="12" height="12" rx="3" fill="#667eea" />
                                <path d="M4 7l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <rect x="1.5" y="1.5" width="11" height="11" rx="2.5" stroke="#ccc" />
                              </svg>
                            )}
                          </span>
                        )}
                        <Avatar avatarUrl={user.avatar_url} name={user.name} size="xsmall" />
                        <span className="option-name">{user.name}</span>
                      </div>
                    );
                  })}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSelect;
